<template>
  <div class="auth-page">
    <main class="auth-column" aria-labelledby="login-title">
      <div class="brand">
        <BrandLogo size="md" text="Apolaki" />
      </div>

      <h1 id="login-title">Welcome back</h1>
      <p class="intro">Log in to your Apolaki account to continue.</p>

      <div v-if="errorMessage" class="alert-error" role="alert">
        {{ errorMessage }}
      </div>

      <!-- PRIMARY: single sign-on (Google + Facebook) -->
      <div v-if="userStore.canUseFirebaseAuth" class="oauth-section">
        <OAuthLogin />
      </div>

      <div v-if="userStore.canUseFirebaseAuth" class="divider"><span>or</span></div>

      <!-- SECONDARY: email + password -->
      <form class="password-login" @submit.prevent="handleLogin">
        <div class="field-group">
          <label for="email">Email</label>
          <input
            id="email"
            v-model.trim="email"
            type="email"
            autocomplete="username"
            placeholder="Enter your email address..."
            required
          >
        </div>

        <div class="field-group">
          <label for="password">Password</label>
          <input
            id="password"
            v-model="password"
            type="password"
            autocomplete="current-password"
            placeholder="Enter your password..."
            required
          >
        </div>

        <button class="email-submit" type="submit" :disabled="userStore.loading">
          {{ userStore.loading ? 'Signing in…' : 'Continue with email' }}
        </button>
      </form>

      <p class="aux-links">
        <router-link :to="{ path: '/signup', query: route.query }">Create an account</router-link>
        <span aria-hidden="true">·</span>
        <router-link to="/forgot-password">Forgot password?</router-link>
      </p>

      <p class="policy">
        By continuing, you acknowledge Apolaki's
        <a href="https://apolaki-ph.netlify.app/privacy#terms" target="_blank" rel="noopener">Terms of Service</a>
        and
        <a href="https://apolaki-ph.netlify.app/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
      </p>
    </main>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BrandLogo from '../components/BrandLogo.vue'
import OAuthLogin from '../components/OAuthLogin.vue'
import { useUserStore } from '../stores/userStore'
import { landingFor, safeRedirectTarget } from '../utils/postLogin'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const email = ref('')
const password = ref('')
const errorMessage = computed(() => {
  if (userStore.error) return userStore.error
  return route.query.error ? String(route.query.error) : null
})

async function handleLogin() {
  const profile = await userStore.login(email.value, password.value)
  if (profile) {
    // Client-side navigation keeps the freshly-fetched session in memory
    // (a hard reload would discard it and force a fragile /auth/me restore).
    const redirect = safeRedirectTarget(route)
    if (!profile.onboardingComplete) {
      // Thread the destination through consent onboarding (?next=), which
      // already router.replace()s to route.query.next on completion.
      router.push(redirect ? { path: '/consent', query: { next: redirect } } : '/consent')
    } else {
      router.push(redirect || landingFor(profile.role))
    }
  }
}
</script>

<style scoped>
/* Notion-like: a clean full-height page with a narrow centered column.
   Reads as an intentional desktop layout at 1440px and works at 390px. */
.auth-page {
  background: #fff;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding: clamp(2rem, 10vh, 7rem) 1.25rem 3rem;
}

.auth-column {
  display: flex;
  flex-direction: column;
  max-width: 340px;
  width: 100%;
}

.brand {
  align-items: center;
  color: var(--kinetic-azure);
  display: flex;
  margin-bottom: 2.25rem;
}

h1 {
  color: #191918;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0 0 0.35rem;
}

.intro {
  color: #78716c;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 1.75rem;
}

.alert-error {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 0.5rem;
  color: #9f1239;
  font-size: 0.875rem;
  margin-bottom: 1.25rem;
  padding: 0.75rem 0.9rem;
}

.oauth-section {
  margin-bottom: 0;
}

.divider {
  align-items: center;
  color: #a8a29e;
  display: flex;
  font-size: 0.8125rem;
  gap: 0.9rem;
  margin: 1.4rem 0;
}

.divider::before,
.divider::after {
  background: #e7e5e4;
  content: '';
  flex: 1;
  height: 1px;
}

.password-login {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

label {
  color: #57534e;
  font-size: 0.8125rem;
  font-weight: 500;
}

input {
  background: #fff;
  border: 1px solid #d6d3d1;
  border-radius: 0.5rem;
  color: #191918;
  font-family: inherit;
  font-size: 0.95rem;
  min-height: 2.75rem;
  padding: 0 0.85rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}

input::placeholder {
  color: #a8a29e;
}

input:focus {
  border-color: var(--kinetic-azure);
  box-shadow: 0 0 0 3px rgba(15, 108, 189, 0.12);
  outline: none;
}

.email-submit {
  background: var(--kinetic-azure);
  border: 0;
  border-radius: 0.5rem;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  margin-top: 0.25rem;
  min-height: 2.75rem;
  transition: background 0.15s;
}

.email-submit:hover:not(:disabled) {
  background: var(--kinetic-azure-dark);
}

.email-submit:disabled {
  cursor: wait;
  opacity: 0.6;
}

.aux-links {
  color: #78716c;
  display: flex;
  font-size: 0.875rem;
  gap: 0.6rem;
  justify-content: center;
  margin: 1.6rem 0 0;
}

.aux-links a {
  color: var(--kinetic-azure);
  font-weight: 500;
  text-decoration: none;
}

.aux-links a:hover {
  text-decoration: underline;
}

.policy {
  color: #a8a29e;
  font-size: 0.75rem;
  line-height: 1.5;
  margin: 2.25rem 0 0;
  text-align: center;
}

.policy a {
  color: #78716c;
  text-decoration: underline;
}

/* ── dark theme ── */
:global(.dark-theme) .auth-page {
  background: #111418;
}

:global(.dark-theme) h1 {
  color: #f1f5f9;
}

:global(.dark-theme) .intro,
:global(.dark-theme) .aux-links {
  color: #94a3b8;
}

:global(.dark-theme) label {
  color: #cbd5e1;
}

:global(.dark-theme) input {
  background: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

:global(.dark-theme) .divider {
  color: #64748b;
}

:global(.dark-theme) .divider::before,
:global(.dark-theme) .divider::after {
  background: #334155;
}

:global(.dark-theme) .policy {
  color: #64748b;
}

:global(.dark-theme) .policy a {
  color: #94a3b8;
}
</style>
